var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { DataFrame } from '../../model/DataFrame.js';
import '../icon/Icon.js';
import '../chart/BasicGraph.js';
import '../table/BasicTable.js';
import './BasicCard.js';
/**
 * Themed card surfacing one KPI dimension within a strategy universe.
 *
 * Built on top of `<basic-card>` for a consistent shell (white surface,
 * border-radius, shadow) and `<basic-icon>` for the icon tile. The
 * accent color (inherited from the parent theme) tints the icon tile,
 * the selected ring and the chart title.
 *
 * Behavior:
 *  - When `selectable` is true the card is clickable; clicking emits
 *    `card-select` (CustomEvent) — the parent decides which card is
 *    selected.
 *  - The chart panel (slot `name="chart"`) smoothly expands /
 *    collapses with `selected`. The panel opts in as soon as one of
 *    `chartTitle`, `graph` or `dataFrame` is set; otherwise it is not
 *    rendered at all.
 *  - When no slotted content is provided, the panel falls back to:
 *    `<basic-graph>` when both `graph` and `dataFrame` are set,
 *    `<basic-table>` when only `dataFrame` is set, or a placeholder.
 *
 * Emits:
 *  - `card-select` (CustomEvent, bubbles, composed) when a selectable
 *    card is clicked. The detail is empty — the parent owns the
 *    selection state.
 */
let KpiDimensionCard = class KpiDimensionCard extends LitElement {
    constructor() {
        super(...arguments);
        this.value = null;
        this.subtitle = null;
        this.chartTitle = null;
        this.icon = null;
        this.iconSvg = null;
        this.accentColor = null;
        this.selectable = false;
        this.selected = false;
        this.graph = null;
        this.dataFrame = null;
    }
    _hexToRgba(hex, alpha) {
        const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
        if (!m)
            return `rgba(156, 163, 175, ${alpha})`;
        const h = m[1];
        const r = parseInt(h.substring(0, 2), 16);
        const g = parseInt(h.substring(2, 4), 16);
        const b = parseInt(h.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    _styleVars() {
        const accent = this.accentColor ?? '#9ca3af';
        return [
            `--accent-color:${accent}`,
            `--accent-soft:${this._hexToRgba(accent, 0.1)}`
        ].join(';');
    }
    _onSelect(e) {
        if (!this.selectable)
            return;
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('card-select', { bubbles: true, composed: true }));
    }
    /** Clicks inside the chart panel — the legend pagination arrows, the
     *  tooltip, the plot itself — must NOT bubble up to the frame's select
     *  handler: otherwise paging a scrolling legend (or poking the chart)
     *  toggles the card and collapses it. The card is (de)selected from its
     *  header, never from within the chart. Recette Magellan 81db839a. */
    _onChartClick(e) {
        e.stopPropagation();
    }
    _renderIcon() {
        if (this.icon) {
            return html `<basic-icon name=${this.icon} size="20"></basic-icon>`;
        }
        if (this.iconSvg) {
            return html `<span class="icon-tile-svg" .innerHTML=${this.iconSvg}></span>`;
        }
        return html `<basic-icon name="layer" size="20"></basic-icon>`;
    }
    _renderChartFallback() {
        if (this.graph && this.dataFrame) {
            return html `
        <basic-graph
          .graph=${this.graph}
          .data=${this.dataFrame}
          .themeColor=${this.accentColor ?? undefined}
          .showTitle=${false}
        ></basic-graph>
      `;
        }
        if (this.dataFrame) {
            return html `
        <basic-table
          .dataFrame=${this.dataFrame}
          selectType="none"
          bare
        ></basic-table>
      `;
        }
        return html `
      <div class="chart-content chart-content--empty">${msg('Chart placeholder', { id: 'kpiDimensionCard.chart.placeholder', desc: 'Placeholder text shown in the chart panel when no graph or data is available' })}</div>
    `;
    }
    render() {
        const hasValue = this.value != null && this.value !== '';
        const hasChart = !!this.chartTitle || !!this.graph || !!this.dataFrame;
        const isGraphMode = !!this.graph && !!this.dataFrame;
        const chartContentClass = isGraphMode
            ? 'chart-content chart-content--graph'
            : 'chart-content';
        return html `
      <div class="frame" style=${this._styleVars()} @click=${this._onSelect}>
        <basic-card size="small">
          <div class="header">
            <span class="icon-tile">${this._renderIcon()}</span>
            <div class="info">
              ${this.subtitle
            ? html `<div class="subtitle" title=${this.subtitle}>
                    ${this.subtitle}
                  </div>`
            : nothing}
              <div class="title" title=${this.title}>${this.title}</div>
            </div>
            <div class="value-cell">
              ${hasValue
            ? html `<span class="value">${this.value}</span>`
            : nothing}
              <slot name="value-sub"></slot>
            </div>
          </div>
          ${hasChart
            ? html `
                <div
                  class="chart"
                  aria-hidden=${!this.selected}
                  @click=${this._onChartClick}
                >
                  <div class=${chartContentClass}>
                    <slot name="chart">${this._renderChartFallback()}</slot>
                  </div>
                </div>
              `
            : nothing}
        </basic-card>
      </div>
    `;
    }
};
KpiDimensionCard.styles = css `
    :host {
      display: block;
      --accent-color: #9ca3af;
      --accent-soft: rgba(156, 163, 175, 0.12);
    }
    .frame {
      border-radius: 12px;
      transition: box-shadow 0.2s ease, transform 0.2s ease;
    }
    :host([selectable]) .frame {
      cursor: pointer;
    }
    :host([selectable]:not([selected])) .frame:hover {
      box-shadow: 0 0 0 1px var(--accent-color), 0 6px 14px rgba(0, 0, 0, 0.06);
      transform: translateY(-1px);
    }
    :host([selected]) .frame {
      box-shadow: 0 0 0 2px var(--accent-color), 0 8px 18px rgba(0, 0, 0, 0.08);
    }
    .header {
      display: grid;
      grid-template-columns: 36px 1fr auto;
      align-items: center;
      gap: 12px;
    }
    .icon-tile {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: var(--accent-color);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
    }
    .icon-tile-svg {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .icon-tile-svg svg {
      width: 100%;
      height: 100%;
    }
    .info {
      min-width: 0;
    }
    .title {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #111827;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .subtitle {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--accent-color);
      margin-bottom: 2px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    /* Value column: the headline number with an optional value-sub
       slot stacked right under it (e.g. a before/after evolution badge). */
    .value-cell {
      justify-self: end;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 2px;
      min-width: 0;
    }
    .value {
      font-size: 22px;
      font-weight: 800;
      letter-spacing: -0.01em;
      color: #111827;
      font-variant-numeric: tabular-nums;
    }
    .chart {
      max-height: 0;
      opacity: 0;
      overflow: hidden;
      margin-top: 0;
      transition:
        max-height 0.35s ease,
        opacity 0.25s ease 0.05s,
        margin-top 0.25s ease;
    }
    :host([selected]) .chart {
      max-height: 320px;
      opacity: 1;
      margin-top: 12px;
    }
    .chart-content {
      min-height: 140px;
      border-radius: 8px;
      background: var(--accent-soft);
      padding: 12px;
      box-sizing: border-box;
    }
    /* When a graph is rendered, drop the tinted background and the
       inner padding so ECharts / Chart.js own the whole space — the
       chart already has its own title/legend chrome and the tint was
       bleeding into the canvas. The explicit height (vs. just
       min-height) is what actually fills the panel: ECharts/Chart.js
       size to their container, and with a fluid min-height the
       container would collapse to the smallest workable size. */
    .chart-content--graph {
      height: 240px;
      min-height: 240px;
      background: transparent;
      padding: 0;
    }
    .chart-content--graph > basic-graph {
      display: block;
      width: 100%;
      height: 100%;
    }
    .chart-content--empty {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #9ca3af;
      font-size: 12px;
      font-style: italic;
    }
    ::slotted(*) {
      display: block;
    }
    basic-card {
      transition: background-color 0.2s ease;
    }
  `;
__decorate([
    property({ type: String })
], KpiDimensionCard.prototype, "title", void 0);
__decorate([
    property()
], KpiDimensionCard.prototype, "value", void 0);
__decorate([
    property({ type: String })
], KpiDimensionCard.prototype, "subtitle", void 0);
__decorate([
    property({ type: String, attribute: 'chart-title' })
], KpiDimensionCard.prototype, "chartTitle", void 0);
__decorate([
    property({ type: String })
], KpiDimensionCard.prototype, "icon", void 0);
__decorate([
    property({ type: String, attribute: 'icon-svg' })
], KpiDimensionCard.prototype, "iconSvg", void 0);
__decorate([
    property({ type: String, attribute: 'accent-color' })
], KpiDimensionCard.prototype, "accentColor", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KpiDimensionCard.prototype, "selectable", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KpiDimensionCard.prototype, "selected", void 0);
__decorate([
    property({ type: Object })
], KpiDimensionCard.prototype, "graph", void 0);
__decorate([
    property({ type: Object, attribute: 'data-frame' })
], KpiDimensionCard.prototype, "dataFrame", void 0);
KpiDimensionCard = __decorate([
    localized(),
    customElement('kpi-dimension-card')
], KpiDimensionCard);
export { KpiDimensionCard };
//# sourceMappingURL=KpiDimensionCard.js.map